import { useState } from "react";
import { C, card } from "../styles";
import { Btn, SP, RAD } from "../ui";
import { REQUIRED_SLOTS, projectCode, stripPidPrefix } from "./SessionCreator";

const formatDate = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
};

const TH_BASE = {
  padding: "12px 20px",
  textAlign: "left",
  fontSize: "12px",
  fontWeight: "600",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  whiteSpace: "nowrap",
};

// Status is shown as a colour dot + neutral label — the same dot-as-state
// language used across the portal (metrics, Outdoor Guide). Neutral text keeps
// AA contrast in both themes; the dot carries the colour.
const STATUS_TONE = {
  "Draft":            C.warning,
  "Waiting for QA":   C.blue,
  "Submitted for QA": C.blue,
  "In Progress":      "#22d3ee",
  "Approved":         C.success,
  "Rejected":         C.danger,
  "Invite Pending":   "#a78bfa",
};
function StatusCell({ status }) {
  const tone = STATUS_TONE[status] || C.warning;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: SP[2], fontSize: "13px", fontWeight: 500, color: C.text1, whiteSpace: "nowrap" }}>
      <span aria-hidden="true" style={{ width: "7px", height: "7px", borderRadius: "50%", background: tone, flexShrink: 0 }} />
      {status || "Draft"}
    </span>
  );
}

function progressBar(session) {
  const filled = REQUIRED_SLOTS.filter(s => session.slots?.[s]?.activeFile).length;
  const total = REQUIRED_SLOTS.length;
  const pct = (filled / total) * 100;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div style={{ width: "64px", height: "5px", background: "var(--border-subtle)", borderRadius: "999px", overflow: "hidden", flexShrink: 0 }}>
        <div style={{ width: `${pct}%`, height: "100%", background: filled === total ? "#10b981" : C.primary, borderRadius: "999px", transition: "width 0.3s" }} />
      </div>
      <span style={{ fontSize: "12px", color: filled === total ? "#10b981" : C.text3, fontWeight: "600", whiteSpace: "nowrap" }}>
        {filled}/{total}
      </span>
    </div>
  );
}

// A row's display status — pending invites read as "Invite Pending".
function displayStatus(s) {
  return s._isPendingInvite ? "Invite Pending" : (s.status || "Draft");
}
// All partner PIDs referenced by a row (accepted + invited).
function rowPartners(s) {
  return [...new Set([...(s.partnerPids || []), ...(s.pendingPartnerPids || [])])];
}

function SubmissionTable({ sessions, pendingInvites = [], projects, filterProject, onFilterChange, sortConfig, onSort, onOpen, onShare, onDelete, onAccept, onDeny, creatorPid, readOnly = false }) {
  const [showHelp, setShowHelp] = useState(false);
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterPartner, setFilterPartner] = useState("All");
  const [filterPaid, setFilterPaid] = useState("All");

  const allRows = [
    ...pendingInvites.map(s => ({ ...s, _isPendingInvite: true })),
    ...sessions,
  ];

  // Distinct options for the filters, derived from the visible rows. Deriving
  // projects from the rows (rather than the caller's project list) means the
  // filter always matches what's actually in the table — including admin
  // view-as, where the caller passes no projects, and projects that are closed
  // to new submissions but still have existing sessions.
  const projectOptions = [...new Set(allRows.map(s => s.project).filter(Boolean))].sort();
  const statusOptions = [...new Set(allRows.map(displayStatus))].sort();
  const partnerOptions = [...new Set(allRows.flatMap(rowPartners))].sort();

  const processed = [...allRows]
    .filter(s => filterProject === "All" || s.project === filterProject)
    .filter(s => filterStatus === "All" || displayStatus(s) === filterStatus)
    .filter(s => filterPartner === "All" || rowPartners(s).includes(filterPartner))
    .filter(s => filterPaid === "All" ||
      (filterPaid === "Paid" ? s.paid === true : s.paid !== true))
    .sort((a, b) => {
      if (a._isPendingInvite && !b._isPendingInvite) return -1;
      if (!a._isPendingInvite && b._isPendingInvite) return 1;
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (sortConfig.key === "createdAt") {
        aVal = aVal?.seconds || 0;
        bVal = bVal?.seconds || 0;
      } else {
        aVal = (aVal || "").toString().toLowerCase();
        bVal = (bVal || "").toString().toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

  const sortArrow = (key) =>
    sortConfig.key === key ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : "";

  const filtersActive = filterProject !== "All" || filterStatus !== "All" ||
    filterPartner !== "All" || filterPaid !== "All";

  return (
    <div className="ui-card" style={{ ...card, overflow: "visible" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: SP[3], flexWrap: "wrap",
        padding: `${SP[5]} ${SP[6]}`,
        borderBottom: "1px solid var(--border-subtle)",
      }}>
        <h2 style={{ fontSize: "16px", fontWeight: 700, color: C.text1, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: SP[2] }}>
          Sessions
          <span style={{
            fontSize: "12px", fontWeight: 600, color: filtersActive ? "var(--og-accent)" : C.text2,
            background: filtersActive ? "var(--primary-soft)" : "var(--surface-2)",
            border: `1px solid ${filtersActive ? "rgba(99,102,241,0.3)" : "var(--border-2)"}`,
            padding: "2px 8px", borderRadius: RAD.sm, whiteSpace: "nowrap",
          }}>
            {filtersActive
              ? `${processed.length} of ${allRows.length}`
              : allRows.length}
          </span>
        </h2>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <select
            className="filter-select"
            value={filterProject}
            onChange={e => onFilterChange(e.target.value)}
            style={{ background: C.surface, border: "1px solid var(--border-2)", borderRadius: "7px", padding: "7px 12px", color: filterProject === "All" ? C.text2 : C.text1, fontSize: "13px", cursor: "pointer" }}
          >
            <option value="All">All Projects</option>
            {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ background: C.surface, border: "1px solid var(--border-2)", borderRadius: "7px", padding: "7px 12px", color: filterStatus === "All" ? C.text2 : C.text1, fontSize: "13px", cursor: "pointer" }}
          >
            <option value="All">All Statuses</option>
            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="filter-select"
            value={filterPartner}
            onChange={e => setFilterPartner(e.target.value)}
            disabled={partnerOptions.length === 0}
            style={{ background: C.surface, border: "1px solid var(--border-2)", borderRadius: "7px", padding: "7px 12px", color: filterPartner === "All" ? C.text2 : C.text1, fontSize: "13px", cursor: partnerOptions.length ? "pointer" : "not-allowed", opacity: partnerOptions.length ? 1 : 0.5 }}
          >
            <option value="All">All Partners</option>
            {partnerOptions.map(p => <option key={p} value={p}>PID {p}</option>)}
          </select>
          <select
            className="filter-select"
            value={filterPaid}
            onChange={e => setFilterPaid(e.target.value)}
            style={{ background: C.surface, border: "1px solid var(--border-2)", borderRadius: "7px", padding: "7px 12px", color: filterPaid === "All" ? C.text2 : C.text1, fontSize: "13px", cursor: "pointer" }}
          >
            <option value="All">All Payments</option>
            <option value="Paid">Paid</option>
            <option value="Unpaid">Not paid</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-scroll-wrapper">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "680px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <th onClick={() => onSort("title")} className="sort-header" style={{ ...TH_BASE, color: C.text2, cursor: "pointer" }}>
                Session{sortArrow("title")}
              </th>
              <th onClick={() => onSort("createdAt")} className="sort-header" style={{ ...TH_BASE, color: C.text2, cursor: "pointer" }}>
                Created{sortArrow("createdAt")}
              </th>
              <th style={{ ...TH_BASE, color: C.text2 }}>Partner</th>
              <th style={{ ...TH_BASE, color: C.text2 }}>Progress</th>
              <th style={{ ...TH_BASE, color: C.text2 }}>Status</th>
              <th style={{ ...TH_BASE, color: "var(--og-accent)", position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  Actions
                  <div
                    onMouseEnter={() => setShowHelp(true)}
                    onMouseLeave={() => setShowHelp(false)}
                    style={{ width: "16px", height: "16px", borderRadius: "50%", background: "var(--border)", border: "1px solid var(--border-2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "700", color: C.text3, cursor: "default", flexShrink: 0 }}
                  >
                    ?
                  </div>
                  {showHelp && (
                    <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 50, background: "var(--card)", border: "1px solid var(--border-2)", borderRadius: "12px", padding: "16px", width: "240px", boxShadow: "0 20px 60px rgba(0,0,0,0.6)", textTransform: "none", letterSpacing: "normal" }}>
                      <div style={{ marginBottom: "10px" }}>
                        <div style={{ fontSize: "12px", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)", color: "#818cf8", padding: "2px 8px", borderRadius: "5px", fontWeight: "600", display: "inline-block", marginBottom: "5px" }}>
                          Open
                        </div>
                        <p style={{ fontSize: "12px", color: C.text3, lineHeight: "1.6", margin: 0, whiteSpace: "normal" }}>
                          Open the session workspace to upload and manage files.
                        </p>
                      </div>
                      <div style={{ height: "1px", background: "var(--border-subtle)", marginBottom: "10px" }} />
                      <div>
                        <div style={{ fontSize: "12px", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.25)", color: "#a78bfa", padding: "2px 8px", borderRadius: "5px", fontWeight: "600", display: "inline-block", marginBottom: "5px" }}>
                          Share
                        </div>
                        <p style={{ fontSize: "12px", color: C.text3, lineHeight: "1.6", margin: 0, whiteSpace: "normal" }}>
                          Add a partner PID to give them access to this session.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {processed.map(session => {
              const rowStatus = session._isPendingInvite ? "Invite Pending" : (session.status || "Draft");
              const isOwn = session.creatorPid === creatorPid;
              return (
                <tr key={session.id} className="table-row">
                  {/* Session title + ID */}
                  <td style={{ padding: "15px 20px", borderBottom: "1px solid var(--surface-2)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "14px", color: C.text1, fontWeight: "500", letterSpacing: "-0.01em" }}>
                            {session.title}
                          </span>
                          {session.paid && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "11px", fontWeight: "700", color: "#10b981", background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.25)", padding: "1px 8px", borderRadius: "999px", whiteSpace: "nowrap" }}>
                              ✓ Paid
                            </span>
                          )}
                        </div>
                        <div
                          title={session.sessionId}
                          style={{ fontSize: "11px", color: C.text3, fontFamily: "monospace", marginTop: "2px" }}
                        >
                          {/* Show real TBD task id once submitted, else live preview. */}
                          {/* Hover for the underlying Firestore session id. */}
                          {session.tbdTaskId
                            || (() => {
                              const c2 = session.partnerPids?.[0] || session.secondCreatorPid || session.creatorPid;
                              const num = session.submissionNumber != null ? session.submissionNumber : "auto";
                              return `${projectCode(session.project)}_P${stripPidPrefix(session.creatorPid)}_P${stripPidPrefix(c2)}_${num}`;
                            })()}
                        </div>
                        <div style={{ fontSize: "12px", color: C.text3, marginTop: "2px" }}>
                          {session.project}
                        </div>
                        {!isOwn && (
                          <span style={{ fontSize: "11px", color: "#a78bfa", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.2)", padding: "2px 8px", borderRadius: "4px", display: "inline-block", marginTop: "4px" }}>
                            Shared by PID {session.creatorPid}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Created */}
                  <td style={{ padding: "15px 20px", fontSize: "14px", color: C.text2, borderBottom: "1px solid var(--surface-2)", whiteSpace: "nowrap" }}>
                    {formatDate(session.createdAt)}
                  </td>

                  {/* Partner */}
                  <td style={{ padding: "15px 20px", borderBottom: "1px solid var(--surface-2)" }}>
                    {(() => {
                      const accepted = session.partnerPids ?? (session.partnerPid ? [session.partnerPid] : []);
                      const pending = session.pendingPartnerPids ?? [];
                      if (!accepted.length && !pending.length) {
                        return <span style={{ fontSize: "13px", color: C.text3 }}>Solo</span>;
                      }
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                          {accepted.map(p => (
                            <span key={p} style={{ fontSize: "12px", color: "#a78bfa", fontWeight: "500" }}>PID {p}</span>
                          ))}
                          {pending.map(p => (
                            <span key={p} style={{ fontSize: "11px", color: C.text3 }}>PID {p} · invited</span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Progress */}
                  <td style={{ padding: "15px 20px", borderBottom: "1px solid var(--surface-2)" }}>
                    {progressBar(session)}
                  </td>

                  {/* Status */}
                  <td style={{ padding: "15px 20px", borderBottom: "1px solid var(--surface-2)" }}>
                    <StatusCell status={rowStatus} />
                  </td>

                  {/* Actions */}
                  <td style={{ padding: "15px 20px", borderBottom: "1px solid var(--surface-2)" }}>
                    {session._isPendingInvite ? (
                      readOnly ? (
                        <span style={{ fontSize: "12px", color: C.text3 }}>Pending invite</span>
                      ) : (
                        <div style={{ display: "flex", gap: SP[2] }}>
                          <Btn variant="danger" size="sm" onClick={() => onDeny(session)}>Deny</Btn>
                          <Btn variant="success" size="sm" onClick={() => onAccept(session)}>Accept</Btn>
                        </div>
                      )
                    ) : (
                      <div style={{ display: "flex", gap: SP[2] }}>
                        <Btn variant="primary" size="sm" onClick={() => onOpen(session)}>Open →</Btn>
                        {!readOnly && isOwn && (
                          <Btn variant="neutral" size="sm" onClick={() => onShare(session)}>Share</Btn>
                        )}
                        {!readOnly && isOwn && session.status === "Draft" && (
                          <Btn variant="danger" size="sm" onClick={() => onDelete(session)}>Delete</Btn>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {processed.length === 0 && (() => {
        return (
          <div style={{ textAlign: "center", padding: "56px 24px" }}>
            <div style={{ fontSize: "32px", marginBottom: "14px", opacity: 0.4 }}>{filtersActive ? "🔍" : "📭"}</div>
            <div style={{ fontSize: "14px", color: C.text3, fontWeight: "500" }}>
              {filtersActive ? "No sessions match these filters" : "No sessions yet"}
            </div>
            {filtersActive ? (
              <button
                onClick={() => { onFilterChange("All"); setFilterStatus("All"); setFilterPartner("All"); setFilterPaid("All"); }}
                style={{ marginTop: "10px", fontSize: "13px", color: C.primary, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: "600" }}
              >
                Clear filters
              </button>
            ) : (
              <div style={{ fontSize: "13px", color: "rgba(85,85,106,0.6)", marginTop: "6px" }}>Create your first session above</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default SubmissionTable;
